import { useApolloClient } from "@apollo/client";
import { CardContainer, LabeledValue, Modal, ModalOpenButton } from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { NavigateNext as NavigateNextIcon } from "@mui/icons-material";
import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { differenceWith, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import {
  IntegrationId,
  namedOperations,
} from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import { FormattedIntegerField } from "../../../commons/form-fields/FormattedIntegerField.js";
import { ManualSave } from "../../../commons/form-fields/ManualSave.js";
import { PasswordField } from "../../../commons/form-fields/PasswordField.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import {
  M1ApiLeadResponseDocument,
  M1ApiLeadResponseQuery,
  M1ApiLeadResponseQueryVariables,
  OrganisationIntegrationsBox_IntegrationFragment,
  OrganisationIntegrationsBox_OrganisationIntegrationFragment,
  useGetPdsBearerTokenQuery,
  useOrganisationIntegrationsBoxAddMutation,
  useOrganisationIntegrationsBoxRemoveMutation,
  useOrganisationIntegrationsBoxUpdateMutation,
  useSetPdsBearerTokenMutation,
} from "./OrganisationIntegrationsBox.generated.js";

type PdsIntegrationFormProps = {
  organisationIntegration: OrganisationIntegrationsBox_OrganisationIntegrationFragment;
  handleUpdate: (
    integrationId: IntegrationId,
    values: { foreignAccountId: string | null; settings: any }
  ) => Promise<void>;
  setPdsBearerToken: ReturnType<typeof useSetPdsBearerTokenMutation>[0];
  t: ReturnType<typeof useTranslate>["t"];
};

type PdsIntegrationModalFieldsProps = {
  isVisible: boolean;
  t: ReturnType<typeof useTranslate>["t"];
};

const PdsIntegrationForm = ({
  organisationIntegration,
  handleUpdate,
  setPdsBearerToken,
  t,
}: PdsIntegrationFormProps) => {
  const { data: pdsTokenData } = useGetPdsBearerTokenQuery({
    fetchPolicy: "network-only",
  });

  const initialBearerToken =
    pdsTokenData?.getPdsBearerToken.__typename === "GetPdsBearerTokenResult"
      ? (pdsTokenData.getPdsBearerToken.bearerToken ?? "")
      : "";

  return (
    <Formik<{
      foreignAccountId: string | null;
      settings: string;
      bearerToken: string;
    }>
      key={`${organisationIntegration.id}-${initialBearerToken}`}
      initialValues={{
        foreignAccountId: organisationIntegration.foreignAccountId || null,
        settings: JSON.stringify(organisationIntegration.settings, null, 2),
        bearerToken: initialBearerToken,
      }}
      onSubmit={async (values, helpers) => {
        await handleUpdate(organisationIntegration.integration.integrationId, {
          foreignAccountId: values.foreignAccountId,
          settings: JSON.parse(values.settings),
        });

        await setPdsBearerToken({
          variables: {
            token: {
              bearerToken: values.bearerToken ?? "",
            },
          },
        });
        helpers.resetForm({
          values: {
            foreignAccountId: values.foreignAccountId,
            settings: values.settings, // or JSON.stringify(savedSettings, null, 2)
            bearerToken: values.bearerToken, // or "" if you want to clear it
          },
        });
      }}
    >
      {formikProps => (
        <Form>
          <Stack direction="column" spacing={1}>
            <TextField
              label={t("Settings", {
                ns: "OrganisationSettings",
              })}
              name="settings"
              required
              multiline
              maxRows={12}
            />

            <PasswordField
              label={
                t("Authentication Token", {
                  ns: "OrganisationSettings",
                }) as string
              }
              name="bearerToken"
            />

            <ManualSave
              onCancel={() => {
                formikProps.handleReset();
              }}
              disabled={formikProps.isSubmitting}
              disabledCancel={!formikProps.dirty}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

const PdsIntegrationModalFields = ({
  isVisible,
  t,
}: PdsIntegrationModalFieldsProps) => {
  if (!isVisible) {
    return null;
  }

  return (
    <PasswordField
      label={
        t("Authentication Token", {
          ns: "OrganisationSettings",
        }) as string
      }
      name="bearerToken"
    />
  );
};

export const OrganisationIntegrationsBox = ({
  organisationId,
  userId,
  allAvailableIntegrations,
  organisationIntegrations,
}: {
  organisationId: string;
  userId: string;
  allAvailableIntegrations: OrganisationIntegrationsBox_IntegrationFragment[];
  organisationIntegrations: OrganisationIntegrationsBox_OrganisationIntegrationFragment[];
}) => {
  const { t } = useTranslate(["OrganisationSettings"]);
  const viewer = useUserData().currentUser!;
  const client = useApolloClient();

  const refetchIntegrationQueries = React.useCallback(async () => {
    await client.refetchQueries({
      include: [
        namedOperations.Query.OrganisationSettingsIntegrations,
        namedOperations.Query.Me,
      ],
    });
  }, [client]);

  const availableIntegrations = React.useMemo(() => {
    return differenceWith(
      allAvailableIntegrations,
      organisationIntegrations,
      (integration, organisationIntegrations) =>
        integration.id === organisationIntegrations.integration.id
    );
  }, [allAvailableIntegrations, organisationIntegrations]);

  const [addOrganisationIntegration, { loading: addLoading }] =
    useOrganisationIntegrationsBoxAddMutation({ client });
  const [removeOrganisationIntegration, { loading: removeLoading }] =
    useOrganisationIntegrationsBoxRemoveMutation({ client });
  const [updateOrganisationIntegration, { loading: updateLoading }] =
    useOrganisationIntegrationsBoxUpdateMutation({ client });
  const [setPdsBearerToken] = useSetPdsBearerTokenMutation({ client });

  const handleAdd = async (values: {
    integrationId: IntegrationId;
    foreignAccountId: string | null;
    bearerToken?: string;
  }) => {
    const isPds = values.integrationId === "pds";

    await addOrganisationIntegration({
      variables: {
        input: {
          integrationId: values.integrationId,
          foreignAccountId: values.foreignAccountId,
        },
      },
      ...(isPds
        ? {}
        : {
            refetchQueries: [
              namedOperations.Query.OrganisationSettingsIntegrations,
              namedOperations.Query.Me,
            ],
            awaitRefetchQueries: true,
          }),
    });

    if (isPds) {
      await setPdsBearerToken({
        variables: {
          token: {
            bearerToken: values.bearerToken ?? "",
          },
        },
      });

      await refetchIntegrationQueries();
    }
  };

  const handleRemove = async (integrationId: IntegrationId) => {
    await removeOrganisationIntegration({
      variables: {
        input: { integrationId },
      },
      refetchQueries: [
        namedOperations.Query.OrganisationSettingsIntegrations,
        namedOperations.Query.Me,
      ],
      awaitRefetchQueries: true,
    });
  };

  const handleUpdate = async (
    integrationId: IntegrationId,
    values: {
      foreignAccountId: string | null;
      settings: {
        m1CalculatorId2MsysTemplateId: {};
        m1CalculatorIdCreateM1Import: {};
      };
    }
  ) => {
    await updateOrganisationIntegration({
      variables: {
        input: {
          integrationId: integrationId,
          foreignAccountId: values.foreignAccountId,
          settings: values.settings,
        },
      },
      refetchQueries: [
        namedOperations.Query.OrganisationSettingsIntegrations,
        namedOperations.Query.Me,
      ],
      awaitRefetchQueries: true,
    });
  };

  return (
    <CardContainer
      title={t("Integrations", {
        ns: "OrganisationSettings",
      })}
      isInitiallyClosed={false}
      itemCount={organisationIntegrations.length}
    >
      <Stack direction="column" spacing={1} py={1}>
        <Box px={1}>
          <LabeledValue
            label={t("MeisterSystems organisation ID", {
              ns: "OrganisationSettings",
            })}
          >
            {organisationId}
          </LabeledValue>
        </Box>
        <Box px={1}>
          <LabeledValue
            label={t("MeisterSystems user ID", {
              ns: "OrganisationSettings",
            })}
          >
            {userId}
          </LabeledValue>
        </Box>
        <Divider />
        {organisationIntegrations.map((organisationIntegration, index) => (
          <Stack
            direction="column"
            spacing={1}
            px={1}
            key={organisationIntegration.id}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="h3">
                {organisationIntegration.integration.name}
              </Typography>

              <IconButton
                size="small"
                color="primary"
                onClick={() =>
                  handleRemove(
                    organisationIntegration.integration.integrationId
                  )
                }
                disabled={removeLoading}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>

            {organisationIntegration.integration.integrationId === "pds" ? (
              <PdsIntegrationForm
                organisationIntegration={organisationIntegration}
                handleUpdate={handleUpdate}
                setPdsBearerToken={setPdsBearerToken}
                t={t}
              />
            ) : (
              <Formik<{
                foreignAccountId: string | null;
                settings: string;
              }>
                initialValues={{
                  foreignAccountId:
                    organisationIntegration.foreignAccountId || null,
                  settings: JSON.stringify(
                    organisationIntegration.settings,
                    null,
                    2
                  ),
                }}
                onSubmit={async values => {
                  await handleUpdate(
                    organisationIntegration.integration.integrationId,
                    {
                      foreignAccountId: values.foreignAccountId,
                      settings: JSON.parse(values.settings),
                    }
                  );
                }}
                enableReinitialize
              >
                {formikProps => (
                  <Form>
                    <Stack direction="column" spacing={1}>
                      {organisationIntegration.integration
                        .isForeignAccountIdRequired && (
                        <TextField
                          label={t("Foreign account ID", {
                            ns: "OrganisationSettings",
                          })}
                          name="foreignAccountId"
                          required
                        />
                      )}
                      <TextField
                        label={t("Settings", {
                          ns: "OrganisationSettings",
                        })}
                        name="settings"
                        required
                        multiline
                        maxRows={12}
                      />
                      <ManualSave
                        onCancel={formikProps.handleReset}
                        disabled={formikProps.isSubmitting}
                        disabledCancel={!formikProps.dirty}
                      />
                    </Stack>
                  </Form>
                )}
              </Formik>
            )}

            {organisationIntegration.integration.id === "meister1" && (
              <>
                <Box>
                  <ModalOpenButton Modal={InspectM1LeadResponseModal}>
                    <Button
                      size="small"
                      color="secondary"
                      endIcon={<NavigateNextIcon />}
                    >
                      {t("Inspect lead response", {
                        ns: "OrganisationSettings",
                      })}
                    </Button>
                  </ModalOpenButton>
                </Box>
                <Box>
                  <Button
                    size="small"
                    color="secondary"
                    endIcon={<OpenInNewIcon />}
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${import.meta.env.VITE_M1_MY_URL}/campaigns`}
                  >
                    {t("Go to campaigns settings", {
                      ns: "OrganisationSettings",
                    })}
                  </Button>
                </Box>
              </>
            )}

            {organisationIntegration.integration.id === "pdf_generator" && (
              <>
                <Box>
                  <Button
                    size="small"
                    color="secondary"
                    endIcon={<OpenInNewIcon />}
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${import.meta.env.VITE_PDF_DASHBOARD_URL}?environment=${import.meta.env.VITE_DEPLOY_ENV}&selectedUserId=${viewer.id}&initialPath=/dashboard`}
                  >
                    {t("Go to templates dashboard", {
                      ns: "OrganisationSettings",
                    })}
                  </Button>
                </Box>
              </>
            )}

            <Divider />
          </Stack>
        ))}

        <Box px={1}>
          <ModalOpenButton
            Modal={AddNewIntegrationModal}
            modalProps={{
              availableIntegrations,
              handleComplete: async ({
                integrationId,
                foreignAccountId,
                bearerToken,
              }) => {
                if (!integrationId) return;
                await handleAdd({
                  integrationId,
                  foreignAccountId,
                  bearerToken,
                });
              },
            }}
          >
            <Button
              type="button"
              color="secondary"
              size="small"
              startIcon={<AddIcon />}
              disabled={availableIntegrations.length === 0 || addLoading}
            >
              {t("Add new integration", {
                ns: "OrganisationSettings",
              })}
            </Button>
          </ModalOpenButton>
        </Box>
      </Stack>
    </CardContainer>
  );
};

const AddNewIntegrationModal = ({
  availableIntegrations,
  handleClose,
  handleComplete,
}: {
  handleClose: () => void;
  handleComplete: (values: {
    integrationId: IntegrationId | null;
    foreignAccountId: string | null;
    bearerToken?: string;
  }) => void | Promise<void>;
  availableIntegrations: OrganisationIntegrationsBox_IntegrationFragment[];
}) => {
  const { t } = useTranslate(["Global", "OrganisationSettings"]);
  const formId = React.useMemo(() => uniqueId(), []);
  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        integration: Yup.object()
          .shape({
            id: Yup.string()
              .label(
                t("Integration name", {
                  ns: "OrganisationSettings",
                })
              )
              .required(),
            isForeignAccountIdRequired: Yup.boolean(),
          })
          .nullable()
          .required(),
        foreignAccountId: Yup.string()
          .label(
            t("Foreign account ID", {
              ns: "OrganisationSettings",
            })
          )
          .nullable()
          .test((value, context) => {
            if (
              context.parent.integration &&
              context.parent.integration.isForeignAccountIdRequired
            ) {
              return !!value;
            }
            return true;
          }),
      }),
    [t]
  );
  return (
    <Formik<{
      integration: {
        id: string;
        integrationId: IntegrationId;
        isForeignAccountIdRequired: boolean;
      } | null;
      foreignAccountId: string | null;
      bearerToken: string;
    }>
      validationSchema={validationSchema}
      initialValues={{
        integration: null,
        foreignAccountId: null,
        bearerToken: "",
      }}
      onSubmit={async values => {
        await handleComplete({
          foreignAccountId: values.foreignAccountId,
          integrationId: values.integration
            ? values.integration.integrationId
            : null,
          bearerToken: values.bearerToken,
        });
        handleClose();
      }}
    >
      {formikProps => {
        const isPdsIntegration =
          formikProps.values.integration?.integrationId === "pds";

        return (
          <Modal
            handleClose={handleClose}
            dialogProps={{ maxWidth: "xs" }}
            title={t("Add new integration", {
              ns: "OrganisationSettings",
            })}
            actionButtons={[
              {
                label: t("Cancel", {
                  ns: "Global",
                }),
                handleClick: handleClose,
                buttonProps: { variant: "text" },
              },
              {
                label: t("Submit", {
                  ns: "Global",
                }),
                buttonProps: {
                  type: "submit",
                  form: formId,
                  loading: formikProps.isSubmitting,
                  disabled: !formikProps.dirty || !formikProps.isValid,
                },
              },
            ]}
          >
            <Form id={formId}>
              <Stack direction="column" spacing={1}>
                <SelectField
                  label={t("Integration name", {
                    ns: "OrganisationSettings",
                  })}
                  name="integration.integrationId"
                  options={availableIntegrations.map(i => ({
                    value: i.id,
                    label: i.name,
                  }))}
                  required
                  onChange={async integrationId => {
                    if (!integrationId) return;
                    const integration =
                      availableIntegrations.find(i => i.id === integrationId) ??
                      null;
                    await formikProps.setValues(values => ({
                      ...values,
                      integration,
                      foreignAccountId: null,
                      bearerToken: "",
                    }));
                  }}
                />
                {formikProps.values.integration &&
                  formikProps.values.integration.isForeignAccountIdRequired && (
                    <TextField
                      label={t("Foreign account ID", {
                        ns: "OrganisationSettings",
                      })}
                      name="foreignAccountId"
                      required
                    />
                  )}
                <PdsIntegrationModalFields isVisible={isPdsIntegration} t={t} />
              </Stack>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

const InspectM1LeadResponseModal = ({
  handleClose,
}: {
  handleClose: () => void;
}) => {
  const { t } = useTranslate(["Global", "OrganisationSettings"]);
  const formId = React.useMemo(() => uniqueId(), []);
  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        leadId: Yup.number()
          .label(
            t("Lead ID", {
              ns: "OrganisationSettings",
            })
          )
          .required(),
      }),
    [t]
  );
  const client = useApolloClient();
  const [response, setResponse] = React.useState<string | null>(null);

  return (
    <Formik<{
      leadId: number;
    }>
      validationSchema={validationSchema}
      initialValues={{
        leadId: 0,
      }}
      onSubmit={async values => {
        const result = await client.query<
          M1ApiLeadResponseQuery,
          M1ApiLeadResponseQueryVariables
        >({
          query: M1ApiLeadResponseDocument,
          variables: {
            leadId: values.leadId,
          },
        });

        console.log(result.data.m1ApiLeadResponse);

        setResponse(
          JSON.stringify(
            JSON.parse(result.data?.m1ApiLeadResponse) ?? null,
            null,
            2
          )
        );
      }}
    >
      {formikProps => (
        <Modal
          handleClose={handleClose}
          dialogProps={{ maxWidth: "md" }}
          title={t("Inspect Meister1 lead response", {
            ns: "OrganisationSettings",
          })}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Submit", {
                ns: "Global",
              }),
              buttonProps: {
                type: "submit",
                form: formId,
                loading: formikProps.isSubmitting,
                disabled: !formikProps.dirty || !formikProps.isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack direction="column" spacing={1}>
              <FormattedIntegerField
                label={t("Lead ID", {
                  ns: "OrganisationSettings",
                })}
                name="leadId"
                required
              />
              {response && (
                <Box
                  position="relative"
                  padding={1}
                  paddingTop={4}
                  sx={{
                    backgroundColor: theme => theme.palette.grey[200],
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    component="pre"
                    fontFamily="monospace"
                    whiteSpace={"pre-wrap"}
                  >
                    {response}
                  </Typography>
                  <IconButton
                    size="small"
                    color="secondary"
                    sx={{ position: "absolute", top: "4px", right: "4px" }}
                    onClick={() => setResponse(null)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
